import React, { useState } from 'react';
import './DititalnaKancelarija.scss';
import { Helmet } from 'react-helmet';
import { useInView } from '../hooks/useInView';

import heroBg from '../Assets/Images/digitaloffice/dk-hero-bg.png';
import heroBgMobile from '../Assets/Images/digitaloffice/dk-hero-bg-mobile.png';
import logoIcon from '../Assets/Images/digitaloffice/dk-icon-logo.svg';
import appsBlobBg from '../Assets/Images/digitaloffice/dk-apps-blob.png';

import iconDocAccess from '../Assets/Images/digitaloffice/dk-icon-doc-access.png';
import iconWorkflows from '../Assets/Images/digitaloffice/dk-icon-workflows.png';
import iconStorage from '../Assets/Images/digitaloffice/dk-icon-storage.png';
import iconTime from '../Assets/Images/digitaloffice/dk-icon-time.png';

import appLeave from '../Assets/Images/digitaloffice/dk-app-leave.png';
import appApproval from '../Assets/Images/digitaloffice/dk-app-approval.png';
import appLedger from '../Assets/Images/digitaloffice/dk-app-ledger.png';
import appSigning from '../Assets/Images/digitaloffice/dk-app-signing.png';
import appActivity from '../Assets/Images/digitaloffice/dk-app-activity.png';

const problemItems = [
  {
    icon: iconDocAccess,
    title: 'Document accessibility',
    desc: 'process documents available anytime, anywhere',
  },
  {
    icon: iconWorkflows,
    title: 'Paper document workflows',
    desc: 'replaced by electronic ones',
  },
  {
    icon: iconStorage,
    title: 'Paper document storage',
    desc: 'in an easily searchable electronic registry',
  },
  {
    icon: iconTime,
    title: 'Time savings',
    desc: 'and environmental preservation',
  },
];

const appItems = [
  {
    icon: appLeave,
    title: 'Employee leave and absences',
    desc: 'Straightforward submission and approval of leave requests, with a record of available days, a team calendar, and automated scheduling of annual leave in compliance with the Labor Law.',
  },
  {
    icon: appApproval,
    title: 'Approval of various request types',
    desc: 'Employees submit requests through an intuitive form, while recipients receive notifications and gain a clear overview of the details needed to approve or decline the request.',
  },
  {
    icon: appLedger,
    title: 'Records ledger',
    desc: 'An electronic registry of documentation that enables straightforward record-keeping, document scanning, and electronic storage. It improves operational efficiency and makes stored documents easier to search and access.',
  },
  {
    icon: appSigning,
    title: 'Digital electronic signing workflow',
    desc: 'Enables a traceable document validation flow within the organization, with simple sending, receiving, and signing of documents.',
  },
  {
    icon: appActivity,
    title: 'Records of work activity engagement',
    desc: 'Employees log the exact time spent on each task, enabling efficient management aligned with business needs and resources.',
  },
];

function DigitalnaKancelarija() {
  const [expandedApp, setExpandedApp] = useState<number | null>(null);

  const { ref: heroRef, visible: heroVisible } = useInView(0.05);
  const { ref: problemsRef, visible: problemsVisible } = useInView();
  const { ref: appsRef, visible: appsVisible } = useInView();
  const { ref: ctaRef, visible: ctaVisible } = useInView();

  return (
    <div className="dk-page">
      <Helmet>
        <title>Artifit - Digital Office</title>
        <meta
          name="description"
          content="A suite of tools built on Microsoft Office 365 for streamlining administrative processes within your organization."
        />
      </Helmet>

      {/* ── HERO ── */}
      <section className="dk-hero">
        <img src={heroBg} alt="" className="dk-hero-bg dk-hero-bg--desktop" />
        <img src={heroBgMobile} alt="" className="dk-hero-bg dk-hero-bg--mobile" />
        <div
          className={`dk-hero-content anim-fade-up${heroVisible ? ' is-visible' : ''}`}
          ref={heroRef as React.RefObject<HTMLDivElement>}
        >
          <div className="dk-hero-logo">
            <img src={logoIcon} alt="Digital Office" className="dk-hero-logo-icon" />
          </div>
          <h1 className="dk-hero-title">
            <span className="dk-orange">Digital</span>{' office'}
          </h1>
          <p className="dk-hero-desc">
            A suite of tools built on Microsoft Office 365 for streamlining administrative
            processes within your organization
          </p>
          <a href="mailto:office@artifit.rs" rel="noreferrer" target="_blank" className="dk-btn-primary">
            Get in touch
          </a>
        </div>
      </section>

      {/* ── WHAT PROBLEMS ── */}
      <section className="dk-problems">
        <div
          className={`dk-section-inner anim-fade-up${problemsVisible ? ' is-visible' : ''}`}
          ref={problemsRef as React.RefObject<HTMLDivElement>}
        >
          <h2 className="dk-section-title">
            <span className="dk-orange">What problems</span><br />
            does it solve?
          </h2>
          <div className="dk-problems-grid">
            {problemItems.map((item, i) => (
              <div className="dk-problem-item" key={i}>
                <img src={item.icon} alt={item.title} className="dk-problem-icon" />
                <p>
                  <strong>{item.title}</strong>{' '}{item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DIGITAL OFFICE APPS ── */}
      <section className="dk-apps">
        <img src={appsBlobBg} alt="" className="dk-apps-blob" />
        <div
          className={`dk-section-inner anim-fade-up${appsVisible ? ' is-visible' : ''}`}
          ref={appsRef as React.RefObject<HTMLDivElement>}
        >
          <h2 className="dk-section-title">
            Digital <span className="dk-orange">Office Apps</span>
          </h2>

          {/* Desktop grid: 3 + 2 */}
          <div className="dk-apps-desktop">
            <div className="dk-apps-row">
              {appItems.slice(0, 3).map((app, i) => (
                <div className="dk-app-card" key={i}>
                  <div className="dk-app-card-inner">
                    <div className="dk-app-card-front">
                      <img src={app.icon} alt={app.title} className="dk-app-icon" />
                      <h3>{app.title}</h3>
                    </div>
                    <div className="dk-app-card-back">
                      <p className="dk-app-card-desc">{app.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="dk-apps-row dk-apps-row--center">
              {appItems.slice(3).map((app, i) => (
                <div className="dk-app-card" key={i + 3}>
                  <div className="dk-app-card-inner">
                    <div className="dk-app-card-front">
                      <img src={app.icon} alt={app.title} className="dk-app-icon" />
                      <h3>{app.title}</h3>
                    </div>
                    <div className="dk-app-card-back">
                      <p className="dk-app-card-desc">{app.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile list */}
          <div className="dk-apps-mobile">
            {appItems.map((app, i) => (
              <div
                className={`dk-apps-item${expandedApp === i ? ' dk-apps-item--open' : ''}`}
                key={i}
                onClick={() => setExpandedApp(expandedApp === i ? null : i)}
              >
                <div className="dk-apps-item-row">
                  <img src={app.icon} alt={app.title} className="dk-apps-item-icon" />
                  <span className="dk-apps-item-title">{app.title}</span>
                  <svg
                    className="dk-apps-item-chevron"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  >
                    <path
                      d="M4 7l5 5 5-5"
                      stroke="#ef4223"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="dk-apps-item-desc">{app.desc}</p>
              </div>
            ))}
          </div>

          <p className="dk-price">
            <span>Price per license: </span>
            <span className="dk-orange">570 RSD/month + VAT</span>
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="dk-cta">
        <div
          className={`dk-cta-inner anim-fade-up${ctaVisible ? ' is-visible' : ''}`}
          ref={ctaRef as React.RefObject<HTMLDivElement>}
        >
          <p className="dk-cta-text">
            Simple tools change how work gets done. Let's set up yours.
          </p>
          <a href="mailto:office@artifit.rs" rel="noreferrer" target="_blank">
            <button className="dk-btn-cta">Get in touch</button>
          </a>
        </div>
      </section>
    </div>
  );
}

export default DigitalnaKancelarija;
